import React, { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { ThemeProvider, StyledEngineProvider, Grid, Tabs, Tab } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import { LinkButton } from 'ui/Buttons';
import { eventsCategories } from 'modules/events/constants/eventsCategories';
import { formatDate } from 'shared/functions';
import { Event } from 'modules/events/types/eventsTypes';
import { ShopToolbar } from 'modules/shop/components/ShopToolbar';

const getMuiTheme = () => ({
  components: {
    MUIDataTable: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
        },
      }
    },
    MUIDataTableToolbarSelect: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          height: 64,
          backgroundColor: '#fff',
        },
        title: {
          minWidth: 200,
        },
      }
    },
    MUIDataTableSearch: {
      styleOverrides: {
        main: {
          '& input': {
            padding: '8px 10px',
          },
        },
        searchText: {
          marginTop: 8,
        },
        searchIcon: {
          marginTop: 15,
        },
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        fixedHeader: {
          zIndex: 1,
        },
      }
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        headerCell: {
          zIndex: 1,
        },
        fixedLeft: {
          zIndex: 1,
        }
      }
    }
  },
});

const useStyles = makeStyles(theme => ({
  toolbarContainer: {
    marginRight: theme.spacing(3),
  },
}));

export type ToolbarAction = {
  name: string;
  action: () => void;
};

type EventsSelectionListProps = {
  events: Event[];
  columns: string[];
  onSelectionChange: (items: Event[]) => void;
  showActive: boolean;
  setShowActive: (value: boolean) => void;
  actions: ToolbarAction[];
  toolbar: JSX.Element;
  editMode: boolean;
};

type MuiSelection = {
  index: number;
  dataIndex: number;
};

export const EventsSelectionList: FC<EventsSelectionListProps> = ({
  events,
  columns,
  onSelectionChange = () => {},
  showActive,
  setShowActive,
  actions = [],
  toolbar = null,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);

  const activeEvents = events.filter(({ status }) => status === 'ACTIVE');
  const inactiveEvents = events.filter(({ status }) => status === 'DRAFT');

  const handleRowSelect = (_currentSelection: MuiSelection[], items: MuiSelection[]) => {
    const selectedItems = items.map(({ dataIndex }) => dataIndex);

    setSelectedEvents(selectedItems);
    onSelectionChange(
      (showActive ? activeEvents : inactiveEvents).filter((_event, index) =>
        selectedItems.includes(index),
      ),
    );
  };

  const columnsSetup: MUIDataTableColumnDef[] = [
    {
      name: 'id',
      label: t('ID'),
      options: {
        display: columns.includes('id') ? 'true' : 'false',
      },
    },
    {
      name: 'title',
      label: t('Title'),
      options: {
        display: columns.includes('title') ? 'true' : 'false',
        sort: true,
        customBodyRender: (value: string, { rowData }: any) => (
          <LinkButton to={`/event-panel/edit/${rowData[0]}`} label={value} />
        ),
      },
    },
    {
      name: 'eventDate',
      label: t('Date'),
      options: {
        display: columns.includes('eventDate') ? 'true' : 'false',
        sort: true,
        customBodyRender: (value: string) => formatDate(new Date(value)),
      },
    },
    {
      name: 'category',
      label: t('Category'),
      options: {
        display: columns.includes('category') ? 'true' : 'false',
        sort: true,
        customBodyRender: (value: keyof typeof eventsCategories) => eventsCategories[value],
      },
    },
    {
      name: 'organiser',
      label: t('Source'),
      options: {
        display: columns.includes('organiser') ? 'true' : 'false',
        sort: true,
      },
    },
  ];

  const title = (
    <Tabs value={showActive ? 0 : 1} onChange={(_, value) => setShowActive(!value)}>
      <Tab label={`Active (${activeEvents.length})`} />
      <Tab label={`Inactive (${inactiveEvents.length})`} />
    </Tabs>
  );

  const optionsSetup: MUIDataTableOptions = {
    filterType: 'checkbox',
    print: false,
    download: false,
    filter: false,
    search: false,
    viewColumns: false,
    pagination: false,
    onRowSelectionChange: handleRowSelect,
    rowsSelected: selectedEvents,
    customToolbar: () => toolbar,
    customToolbarSelect: () => (
      <Grid container justifyContent="flex-end" spacing={1} className={classes.toolbarContainer}>
        {toolbar && <Grid item>{toolbar}</Grid>}
        {actions.length !== 0 && (
          <Grid item>
            <ShopToolbar actions={actions} />
          </Grid>
        )}
      </Grid>
    ),
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title={title}
          data={showActive ? activeEvents : inactiveEvents}
          columns={columnsSetup}
          options={optionsSetup}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
